import cloneDeep from 'lodash.clonedeep';
import { formatConversationOnEvents } from '../utils/conversation';

const DELEGATE_OPTIONS = ['beforeDisplay', 'beforeSend', 'beforePostbackSend', 'onInvalidAuth'];

export class Delegate {
    constructor() {
        this.delegate = {};
    }

    setDelegate(delegate) {
        if (typeof delegate !== 'object') {
            throw new Error('setDelegate failed: delegate is not an object');
        }

        const invalidDelegate = Object.keys(delegate).find(
            (delegateOption) => !DELEGATE_OPTIONS.includes(delegateOption),
        );

        if (invalidDelegate !== undefined) {
            throw new Error(`setDelegate failed: invalid delegate option: ${invalidDelegate}`);
        }

        Object.keys(delegate).forEach((delegateOption) => {
            const delegateFunc = delegate[delegateOption];
            if (typeof delegateFunc !== 'function' && delegateFunc !== null && delegateFunc !== undefined) {
                throw new Error(`setDelegate failed: delegate option ${delegateOption} is an invalid type`);
            }
        });

        this.delegate = delegate;
    }

    beforeDisplay(message, conversation) {
        let result = message;

        if (typeof this.delegate.beforeDisplay === 'function') {
            try {
                result = this.delegate.beforeDisplay(cloneDeep(message), {
                    conversation: formatConversationOnEvents(conversation),
                });
            } catch (error) {
                console.error("Could not apply the 'beforeDisplay' transformation.");
                console.error(error);
                return message;
            }

            if (result) {
                result._id = message._id;
                result._isModifiedBeforeDisplay = true;
            } else {
                result = null;
            }
        }

        return result;
    }

    beforeSend(message, conversation) {
        let result = message;

        if (typeof this.delegate.beforeSend === 'function') {
            result = cloneDeep(message);
            const update = this.delegate.beforeSend(message, {
                conversation: formatConversationOnEvents(conversation),
            });
            return Object.assign(result, update);
        }

        return result;
    }

    beforePostbackSend(postback, conversation) {
        let result = postback;

        if (typeof this.delegate.beforePostbackSend === 'function') {
            result = cloneDeep(postback);
            const update = this.delegate.beforePostbackSend(postback, {
                conversation: formatConversationOnEvents(conversation),
            });
            return Object.assign(result, update);
        }

        return result;
    }

    hasBeforeDisplay() {
        return !!(this.delegate.beforeDisplay && typeof this.delegate.beforeDisplay === 'function');
    }

    hasOnInvalidAuth() {
        return !!(this.delegate.onInvalidAuth && typeof this.delegate.onInvalidAuth === 'function');
    }

    onInvalidAuth() {
        if (this.hasOnInvalidAuth()) {
            const jwt = this.delegate.onInvalidAuth();

            if (!jwt || typeof jwt === 'string' || typeof jwt.then === 'function') {
                return Promise.resolve(jwt);
            }

            throw new Error('onInvalidAuth must return a JWT string or a Promise<String> that resolves to the JWT.');
        }

        return Promise.resolve();
    }
}

export const delegate = new Delegate();
